import React, { FC, useState, useEffect } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Button, Form, Radio, Input, TextArea, Header } from 'semantic-ui-react'
import { useDatasetsCreator } from '../../contexts/DatasetsCreatorContext'
import Section from '../../components/common/Section'
import SubSection from '../../components/common/SubSection'
import Title from '../../components/common/Title'
import UploadCSVModal from '../../components/modals/UploadCSVModal'
import CustomLabel from '../../components/common/custom-label/CustomLabel'
import { HelpDocsLink } from '../../components/common/ExternalLink'

import './CreateDataset.scss'

const CreateDataset: FC<IProps> = ({ history }) => {
  const {
    formData,
    updateFormData,
    setCurrentStep,
    setSelectedColumns,
    resetState,
    uploadCSV,
    transposeData,
    originalCsvData,
  } = useDatasetsCreator()

  if (!updateFormData || !setCurrentStep || !setSelectedColumns || !resetState || !uploadCSV || !transposeData) {
    throw new Error('DatasetsCreator context was not initialized properly')
  }

  // Form state
  const [title, setTitle] = useState<string>(formData?.title || '')
  const [description, setDescription] = useState<string>(formData?.description || '')
  const [hasHeaders, setHasHeaders] = useState<boolean>(formData?.hasHeader || false)
  const [dataConnection, setDataConnection] = useState<string>(formData?.dataType || 'row')

  // Validate if we have CSV data, if not show modal
  useEffect(() => {
    if (!originalCsvData?.length) {
      setCurrentStep('upload')
    }
  }, [originalCsvData, setCurrentStep])

  useEffect(() => {
    if (formData.hasHeader !== hasHeaders || formData.dataType !== dataConnection) {
      setSelectedColumns([])
    }
  }, [dataConnection, formData.dataType, formData.hasHeader, hasHeaders, setSelectedColumns])

  // Back button handler
  const handleBack = () => {
    resetState()
    history.push('/datasets')
  }

  // Next button handler
  const handleNext = () => {
    updateFormData({
      title,
      description,
      hasHeader: hasHeaders,
      dataType: dataConnection,
    })

    if (dataConnection === 'column') {
      const transposedData = transposeData(originalCsvData)
      uploadCSV(transposedData)
    } else {
      uploadCSV(originalCsvData)
    }
    history.push('/preview-dataset')
  }

  // Form field validation
  const isFormValid = () => {
    return title.trim() !== ''
  }

  return (
    <Section>
      <>
        <Title title="My Datasets">
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button onClick={handleBack}>Back</Button>
            <Button primary disabled={!isFormValid()} onClick={handleNext} style={{ backgroundColor: '#5c50e6' }}>
              Next
            </Button>
          </div>
        </Title>

        {originalCsvData?.length > 0 && (
          <SubSection hasGreyBackground>
            <>
              <Header as="h1" style={{ marginBottom: '2rem', textAlign: 'left', fontWeight: 'bold' }}>
                Dataset information
                <Header.Subheader>
                  Tell us about your data and how it's arranged so we can set it up correctly. Give your dataset a
                  unique title and/or description so it’s easy to find later.
                </Header.Subheader>
              </Header>
              <div className="ui segment">
                <Form>
                  <Form.Field required>
                    <div style={{ display: 'flex' }}>
                      <label>Title</label>
                      <CustomLabel.Popup popupContent="e.g. “Customer Campaign Spring 2025” or “Employee Directory”" />
                    </div>
                    <Input
                      placeholder="Give your dataset a unique name"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </Form.Field>

                  <Form.Field>
                    <label>Description (optional)</label>
                    <TextArea
                      placeholder="Add context to help your team recognize this dataset later"
                      value={description}
                      onChange={(e, data) => setDescription(data.value as string)}
                      style={{ minHeight: '100px' }}
                    />
                  </Form.Field>

                  <Form.Field className="radio">
                    <label htmlFor="hasHeaders">Does your file include headers?</label>
                    <Radio
                      label="Yes, the first row contains column names"
                      name="hasHeaders"
                      checked={hasHeaders === true}
                      onChange={() => setHasHeaders(true)}
                    />
                    <Radio
                      label="No"
                      name="hasHeaders"
                      checked={hasHeaders === false}
                      onChange={() => setHasHeaders(false)}
                    />
                  </Form.Field>

                  <Form.Field className="radio">
                    <label htmlFor="dataConnect">How should we interpret your data?</label>
                    <div>
                      <Radio
                        label="Each row is one record (recommended)"
                        name="dataConnection"
                        checked={dataConnection === 'row'}
                        onChange={() => setDataConnection('row')}
                      />
                      <CustomLabel.Popup popupContent="Match each record’s details from a single row using a unique ID" />
                    </div>
                    <div>
                      <Radio
                        label="Each column is one record"
                        name="dataConnection"
                        checked={dataConnection === 'column'}
                        onChange={() => setDataConnection('column')}
                      />
                      <CustomLabel.Popup popupContent="Use this only if each column contains the data related to a single record" />
                    </div>

                    <div className="description">
                      Most users select the <strong>row-based</strong> format. Make sure your CSV file includes a column
                      (or row, for column-based formats) with unique identifiers, such as an email address or staff ID.{' '}
                      <HelpDocsLink documentTag="how-to-format-a-csv-dataset-for-use-in-vloggi">
                        See our formatting guide
                      </HelpDocsLink>
                      .
                    </div>
                  </Form.Field>
                </Form>
              </div>

              <Header as="h3" style={{ fontWeight: 'bold' }}>
                What happens next?
                <Header.Subheader>
                  In the next step, you’ll choose the <strong>key column</strong>—this is how we’ll look up and pre-fill
                  contributor details (like name, email, or location) across your video projects. Learn{' '}
                  <HelpDocsLink documentTag="how-to-pre-populate-contributor-data-in-vloggi">
                    how you can use datasets in projects
                  </HelpDocsLink>
                  .
                </Header.Subheader>
              </Header>
            </>
          </SubSection>
        )}

        <UploadCSVModal />
      </>
    </Section>
  )
}

interface IProps extends RouteComponentProps {}

export default withRouter(CreateDataset)
